import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { canonicalFor, routeMetaFor, SITE_ORIGIN } from './routeMeta'
import { isPrefixMount } from './editionPath'

/**
 * Keeps the document head honest across client navigation.
 *
 * The build writes a correct `<head>` into each route's HTML file, which is
 * what a crawler and a link-preview fetcher receive. But once the app has
 * booted, a tap from the landing page to /register never asks the server for
 * anything — so without this the title, description and canonical would stay
 * whatever the first-loaded document said. Both halves read the same
 * ROUTE_META table, so they cannot disagree.
 *
 * Open Graph tags are updated too. No crawler evaluates them post-hydration,
 * but a user sharing the current page from a browser's own share sheet is
 * reading the live DOM, and a share that carries the previous page's title is
 * a bug the reader sees.
 *
 * A prefix mount is excluded from the index and keeps its own served head — see
 * the note in useRouteMeta.
 */

function setMeta(selector: string, attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(selector)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function setLink(rel: string, href: string) {
  let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!tag) {
    tag = document.createElement('link')
    tag.setAttribute('rel', rel)
    document.head.appendChild(tag)
  }
  tag.setAttribute('href', href)
}

export function useRouteMeta(): void {
  const { pathname } = useLocation()

  useEffect(() => {
    // On a prefix mount (`/chowdash`) the mount's own built document is the only
    // head source, so the route table is not applied at all. Two reasons:
    //
    //  - The table is keyed on the unprefixed path, so /chowdash/inbox would
    //    take the ChooseChow inbox entry and relabel the rider inbox, and would
    //    claim a canonical of https://choosechow.com/inbox — a customer URL.
    //  - A canonical has to be true in the bytes a crawler receives. Rewriting
    //    it after hydration to disagree with the served document is the exact
    //    conflict routeMeta.ts warns about.
    //
    // Only the exclusion is enforced here, because the served bytes cannot be
    // trusted to have carried it: the rider routes have no ROUTE_META entry, so
    // nothing else would ever write a robots directive for them.
    if (typeof window !== 'undefined' && isPrefixMount(window.location.pathname)) {
      setMeta('meta[name="robots"]', 'name', 'robots', 'noindex, follow')
      return
    }

    const meta = routeMetaFor(pathname)
    // An unrouted path gets no metadata rather than the homepage's. Handing the
    // front page's identity to an unknown URL is what makes every stray link a
    // duplicate of it.
    if (!meta) return

    const canonical = canonicalFor(meta.path)
    document.title = meta.title
    setMeta('meta[name="description"]', 'name', 'description', meta.description)
    setMeta('meta[name="robots"]', 'name', 'robots',
      meta.indexable ? 'index,follow,max-image-preview:large,max-snippet:-1' : 'noindex, follow')
    setLink('canonical', canonical)
    setMeta('meta[property="og:title"]', 'property', 'og:title', meta.title)
    setMeta('meta[property="og:description"]', 'property', 'og:description', meta.description)
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonical)
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title)
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', meta.description)
  }, [pathname])
}

/** The absolute URL of a kitchen's public page, for share links inside the app. */
export const kitchenURL = (slug: string): string => `${SITE_ORIGIN}/kitchen/${slug}`

/** The absolute URL of a dish's public page. */
export const dishURL = (slug: string): string => `${SITE_ORIGIN}/dish/${slug}`
